import React from "react";
import PropTypes from "prop-types";
import HcsCaptionControl from "./hcs-captioncontrol";
import thousands from "thousands";

/**
 * @class
 * @classdesc Base control.
 *
 * @param {object} props - Control properties
 * @param {function} [props.onConfig] - Call this function when control changes its configuration. This triggers ECS to update its state.
 * Functions parameters:
 *   - method - ECS method like "setCfgValue", "setSubCfgValue", ...
 *   - attrName - In case of "setCfgValue" and "setSubCfgValue": Name of the attribute
 *   - attrValue - In case of "setCfgValue" and "setSubCfgValue": Value of the attribute
 *   - options - Additional options.
 * For more info see the {@link HcsConfigurator#configure} function.
 * @param {object} [props.configWait] - Promise object which waits for a specifiy ECS event like "setCfgValue".
 * @param {bool|string|number} [props.progressState] - Current progress wait state. "false" is default, "indeterminate" when configurator is loading next ECS state.
 * @param {bool} [renderOuterListControl=true] - Normally every control has a surrounding LI element, because controls are listed inside an UL.
 * When its required to render a control in another context (like using it in other controls) return "false" to only render the control itself without LI.
 */
export default class HcSEdit extends HcsCaptionControl {
    constructor(props) {
        super(props);

        this.state = {
            error: null
        };

        // Reference to input field instance
        this.inputRef = React.createRef();

        this.keyUp = this.keyUp.bind(this);
        this.ecsSetValueDefault = this.ecsSetValueDefault.bind(this);
        this.valueChanged = this.valueChanged.bind(this);
        this.updating = false;
        this.thousandsSeparator = this.props.hcs.settings.get("SETTINGS.CULTURE_THOUSANDS_SEPARATOR");
        this.focusEnabled = this.props.hcs.settings.get("SETTINGS.CONFIGURATION_ENABLE_FOCUS", "bool");
        this.errorMessageTimeout = null;
    }

    keyUp(event) {
        if(event.key == "Enter") {
            this.valueChanged.call(this);
        }
    }

    valueChanged(newValue) {
        let self = this;

        const { data, ecsSetValue } = this.props;
        
        let inputField = this.inputRef.current;

        // ECS value, not the new input value.
        let currentValue = data.info.value;

        // We use the "updating" state to avoid duplicate server callbacks when user changes a value and presses ENTER.
        // In Chrome browsers this will also trigger the "blur" event.
        // To fix this we'll lock this function until state has been successfully updated.
        if(!this.updating) {
            if(this.errorMessageTimeout !== null) {
                clearTimeout(self.errorMessageTimeout);
            }

            let valueNew = newValue || inputField.value;

            if(this.checkValue(valueNew)) {
                valueNew = this.cleanValue(valueNew.trim());
                // Update field value by cleaned value (like thousand removed) without changing the current state
                inputField.value = valueNew;

                // The "!=" comparison is correct here.
                // Don't change this due to JsLint recommendations!
                if(valueNew != currentValue) {
                    (typeof ecsSetValue === "string" ? this[ecsSetValue] : ecsSetValue).call(self, inputField);
                }
            } else {
                // For illegal input values restore the last valid value.
                inputField.value = currentValue;

                // Hide illegal value error message after 2 seconds
                self.errorMessageTimeout = setTimeout(_ => {
                    self.setState({
                        error: null
                    });
                }, 2000);
            }
        }
    }

    ecsSetValueDefault(inputField) {
        let self = this;

        let attrName = inputField.getAttribute("attrname");
        let attrValue = inputField.value;

        this.props.checkValueChange(this, {
            confirmed: _ => {
                self.updating = true;

                self.props.onConfig("setCfgValue", attrName, attrValue, {
                    sender: "HCS_EDIT",
                    success: (result) => {
                        if(result.ecs && result.ecs !== "") {
                            let ecsData = JSON.parse(result.ecs);

                            // After ECS callback read cfg value from ECS and update our state.
                            // This is when the user's value was not correct and has been changed by ECS.
                            // In this case we must update our textbox to display the ECS value.
                            let ecsAttribute = ecsData.cfgState.cfgobj.attributes.find(a => {
                                return a.ident === attrName;
                            });

                            super.triggerConfigurator();
                            self.updating = false;
                        }
                    }
                });
            },
            rejected: _ => {
                inputField.value = currentValue;
            }
        });
    }
    
	// override from base class
	getCcaoData() {
		const { data } = this.props;

		if(data.data.CCAO_DATA) {
			try {
				return JSON.parse(data.data.CCAO_DATA);
			} catch(e) {
                console.error(`Error parsing CCAO_DATA object from ECS for attribute '${data.ident}': ${e.message}: ${value.data.CCAO_DATA}`);
			}
		}

		return null;
	}

    getCaption() {
        const { data } = this.props;
        let caption = super.getCaption();

        switch(data.data.PDC) {
            case "DOUBLE":
            case "INTEGER":
                if(data.info.minincl && data.info.maxincl) {
                    let unitText = "";

                    if(data.flags && data.flags.CFGUnit) {
                        unitText = " " + data.flags.CFGUnit;
                    }

                    caption += " (" + thousands(data.info.min, this.thousandsSeparator) + " -  " + thousands(data.info.max, this.thousandsSeparator) + unitText + ")";
                } else if(data.flags && data.flags.CFGUnit) {
                    caption += " (" + data.flags.CFGUnit + ")";
                }
                break;
        }

        return caption;
    }

    cleanValue(value) {
        const { data } = this.props;

        switch(data.data.PDC) {
            case "INTEGER":
                // Remove 1000er separator dots
                value = value.replace(/\./g, "");
                return value !== "" ? parseInt(value) : value;

            default:
                return value;
        }
    }
    
    checkValue(value) {
        const { data } = this.props;

        switch(data.data.PDC) {
            case "INTEGER":
                value = value.replace(/\./g, "");

                if(value.trim() === "" || (value.trim() !== "" && (isNaN(parseInt(value)) || !RegExp(/^[0-9]*$/).test(value)))) {
                    this.setState({
                        error: this.props.hcs.t.gui("InputError_ValueNotNumber")
                    });
                    return false;
                }

                if(data.info.minincl) {
                    if(parseInt(value) < data.info.min) {
                        this.setState({
                            error: this.props.hcs.t.gui("InputError_ValueTooSmall")
                        });
                        return false;
                    }
                }

                if(data.info.maxincl) {
                    if(parseInt(value) > data.info.max) {
                        this.setState({
                            error: this.props.hcs.t.gui("InputError_ValueTooLarge")
                        });
                        return false;
                    }
                }
                break;
        }

        this.setState({
            error: null
        });
        
        return true;
    }

    getInputType() {
        const { data } = this.props;

        switch(data.data.PDC) {
            case "INTEGER":
                return "number";
             
            default:
                return "text";
        }
    }

    childClass() {
        return "hcs-edit";
    }

    get maxLength() {
        const { data } = this.props;

        if(data.flags && data.flags.CFGTextMaxLength) {
            let maxLen = parseInt(data.flags.CFGTextMaxLength);

            return isNaN(maxLen) ? null : maxLen;
        }

        return null;
    }

    get inputClasses() {
        const { data } = this.props;

        let inputClasses = [];

        if(this.focusEnabled && data.info.focused) {
            inputClasses.push("hcs-focused");
        }

        if(this.state.error !== null) {
            inputClasses.push("hcs-error");
        }

        if(!data.info.guienabled) {
            inputClasses.push("hcs-readonly");
        }

        return inputClasses;
    }

    renderControl() {
        const { data } = this.props;

        // By creating a unique "key" attribute for the outer container
        // we ensure to always render with the current value (as defaultValue) from ECS.
        // The uniqueness is being guaranteed by using either the ident and value.
        // See https://stackoverflow.com/questions/30727837/react-change-input-defaultvalue-by-passing-props/41962233 for details
        return (
            <React.Fragment key={data.ident + "_" + this.inputFieldValue}>
                {
                    this.renderInputField()
                }
                {
                    this.state.error !== null ? <span className="hcs-error">{this.state.error}</span> : null
                }
            </React.Fragment>);
    }

    get inputFieldValue() {
        const { data } = this.props;

        return data.info.value;
    }

    renderInputField() {
        const { data } = this.props;

        let inputClasses = this.inputClasses;

        return <input
            type={this.getInputType()}
            ref={this.inputRef}
            attrname={data.ident}
            defaultValue={this.inputFieldValue}
            className={inputClasses.length > 0 ? inputClasses.join(" ") : null}
            readOnly={super.isEnabled() ? null : "readonly"}
            maxLength={this.maxLength}
            onKeyUp={super.isEnabled() ? this.keyUp : null}
            onBlur={_ => { if(super.isEnabled()) { this.valueChanged.call(this); } }}
            /* Disable changing value via mouse wheel when input type is "number" */
            onWheel={(e) => { if(this.getInputType() === "number") { e.target.blur(); } }}
        />;
    }

    render() {
        return super.render(this.renderControl());
    }
}

HcSEdit.propTypes = {
    hcs: PropTypes.object,
    data: PropTypes.object,
    renderOuterListControl: PropTypes.bool,
    onConfig: PropTypes.func,
    ecsSetValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
    ]),
};

HcSEdit.defaultProps = {
    renderOuterListControl: true,
    onConfig: _ => { },
    ecsSetValue: "ecsSetValueDefault"
};